import { useState, useEffect, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { addDays, format } from "date-fns";
import {
  ChevronLeft,
  MapPin,
  Star,
  Check,
  CalendarIcon,
  ChevronDown,
  Clock,
  Flag,
  Bed,
  Users,
  Info,
  Lock,
} from "lucide-react";
import CourseInfoDrawer from "@/components/golf/CourseInfoDrawer";
import { Calendar } from "@/components/ui/calender";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  calculateCourseRoundPrice,
  calculateGolfTotal,
  deriveTeePeriod,
  formatSGD,
} from "@/utils/pricingEngine";

import { getBookingSettings } from "@/services/settingService";
import { getGateway } from "@/services/gatewayService";
import { getAllCourses } from "@/services/courseService";
import { getSlotBookingsForCourseDate } from "@/services/bookingService";
import RecommendedTravelStep from "../components/packages/RecommendedTravelStep";
import TeeTimePopover from "../components/packages/TeeTimePopover";
import { useSiteTexts } from "@/cms/useSiteText";
import { fillTemplate } from "@/cms/textRegistry";

import { getEarliestTeeTime } from "@/utils/terminalGroups";

const MAX_CUSTOM_ROUNDS = 6;
const DEFAULT_ADVANCE_HOURS = 48;
// Older gateways stored selectedCourses as an object keyed by round index
// (e.g. { 0: [...], 1: [...] }) before it became a flat, shared-pool array.
// Flatten either shape so this page doesn't crash on legacy documents.
function normalizeSelectedCourses(raw) {
  if (Array.isArray(raw)) return raw;
  if (raw && typeof raw === "object") {
    const seen = new Set();
    const flat = [];
    Object.values(raw).forEach((arr) => {
      (arr || []).forEach((c) => {
        if (c && !seen.has(c.id)) {
          seen.add(c.id);
          flat.push(c);
        }
      });
    });
    return flat;
  }
  return [];
}

// Blocked dates are stored as 'yyyy-MM-dd' strings on the course doc.
function isCourseBlockedOnDate(course, date) {
  if (!course?.block_dates_enabled || !date) return false;
  const dateStr = format(date, "yyyy-MM-dd");
  return (course.blocked_dates || []).includes(dateStr);
}

export default function PackageDetail() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const id = searchParams.get("id");

  const [pkg, setPkg] = useState(null);
  const [loadingPkg, setLoadingPkg] = useState(true);
  const [allCourses, setAllCourses] = useState([]);

  const [travelSelections, setTravelSelections] = useState({
    selectedFerry: null,
    selectedFerryTime: null,
    selectedOutboundFerryTime: null,
    selectedTransport: null,
    selectedTransportTime: null,
    selectedOutboundTransport: null,
    selectedOutboundTransportTime: null,
    selectedAccommodation: null,
    sharingType: null,
    numRooms: null,
    checkInDate: null,
    checkInTime: null,
    checkOutDate: null,
  });

  const [activeRound, setActiveRound] = useState(0);
  const [selectedCourses, setSelectedCourses] = useState(
    /** @type {Record<number, any>} */ ({}),
  );
  const [selectedDates, setSelectedDates] = useState(
    /** @type {Record<number, Date | undefined>} */ ({}),
  );
  const [selectedTimes, setSelectedTimes] = useState(
    /** @type {Record<number, string>} */ ({}),
  );
  const [numPlayers, setNumPlayers] = useState(2);
  const [customRounds, setCustomRounds] = useState(3);
  const [slotBookedCounts, setSlotBookedCounts] = useState({});
  const [bookingSettings, setBookingSettings] = useState(null);
  // Course opened via the "i" button on a golf club card — side panel on
  // desktop, bottom sheet on mobile (CourseInfoDrawer handles both).
  const [infoCourse, setInfoCourse] = useState(null);
  // Controls the per-round date popover so it closes as soon as a day is
  // picked, instead of staying open until the user clicks elsewhere.
  const [dateOpen, setDateOpen] = useState(false);
  // Opened automatically right after a golf club is picked for the active
  // round, so the user flows straight into choosing a tee time.
  const [teeTimeOpen, setTeeTimeOpen] = useState(false);
  // Briefly true right after a locked (no-date-yet) club card is clicked —
  // pops the date popover open and pulses its trigger so the "why can't I
  // pick a club" question answers itself instead of the click just doing
  // nothing.
  const [dateAttention, setDateAttention] = useState(false);
  // The tee-time trigger lives in the active-round bar, above the club
  // cards. If that bar is scrolled out of view when a card's "Select" is
  // clicked further down the page, the popover anchors off-screen — scroll
  // the bar back into view before opening it.
  const activeRoundBarRef = useRef(null);
  // Don't carry an open tee-time popover across rounds when the user
  // switches via the round pills.
  useEffect(() => {
    setTeeTimeOpen(false);
  }, [activeRound]);
  const AnyPopoverContent = /** @type {any} */ (PopoverContent);
  const t = useSiteTexts({
    instructionsHeading: "booking.instructionsHeading",
    stepSelectDate: "booking.stepSelectDate",
    stepSelectCourse: "booking.stepSelectCourse",
    stepSelectTime: "booking.stepSelectTime",
    roundHeaderWithCourse: "booking.roundHeaderWithCourse",
    roundDateTrigger: "booking.roundDateTrigger",
    roundTeeTimeTrigger: "booking.roundTeeTimeTrigger",
    pickDateToUnlock: "booking.pickDateToUnlock",
    noClubsAssigned: "booking.noClubsAssigned",
    clubCardLocked: "booking.clubCardLocked",
    clubCardSelected: "booking.clubCardSelected",
    clubCardSelect: "booking.clubCardSelect",
  });
  const travelStepRef = useRef(null);
  const hasAutoScrolledToTravelRef = useRef(false);
  const activePeriod = deriveTeePeriod(selectedTimes[activeRound]);
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  // const firstBookableDate = new Date(today);
  // firstBookableDate.setDate(firstBookableDate.getDate() + 3);

  const numRounds = pkg?.custom ? customRounds : pkg?.rounds || 1;
  const numNights = pkg?.custom
    ? Math.max(0, customRounds - 1)
    : pkg?.nights || 0;

  useEffect(() => {
    (async () => {
      try {
        const data = await getBookingSettings();
        setBookingSettings(data);
      } catch (err) {
        console.error("Failed to load booking settings:", err);
        setBookingSettings({ blocked_advance: DEFAULT_ADVANCE_HOURS });
      }
    })();
  }, []);

  useEffect(() => {
    (async () => {
      setLoadingPkg(true);
      try {
        const result = await getGateway(id);
        setPkg(result || null);
      } catch (err) {
        console.error("Failed to load gateway:", err);
        setPkg(null);
      } finally {
        setLoadingPkg(false);
      }
    })();
  }, [id]);

  useEffect(() => {
    (async () => {
      try {
        const list = await getAllCourses();
        setAllCourses(Array.isArray(list) ? list : []);
      } catch (err) {
        console.error("Failed to load courses:", err);
        setAllCourses([]);
      }
    })();
  }, []);

  // Prefill from homepage search query params (date/players), once pkg is known.
  useEffect(() => {
    if (!pkg) return;
    const playersParam = searchParams.get("players");
    if (playersParam) {
      const n = parseInt(playersParam, 10);
      if (!isNaN(n) && n > 0) setNumPlayers(Math.min(8, Math.max(1, n)));
    }
    const dateParam = searchParams.get("date");
    if (dateParam) {
      const d = new Date(dateParam);
      if (!isNaN(d.getTime())) handleSetDate(0, d);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pkg]);

  useEffect(() => {
    const activeCourseId = selectedCourses[activeRound]?.id;
    const activeDate = selectedDates[activeRound];
    if (!activeCourseId || !activeDate) {
      setSlotBookedCounts({});
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const counts = await getSlotBookingsForCourseDate(
          activeCourseId,
          activeDate,
        );
        if (!cancelled) setSlotBookedCounts(counts);
      } catch (err) {
        console.error("Failed to load slot availability:", err);
        if (!cancelled) setSlotBookedCounts({});
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [activeRound, selectedCourses, selectedDates]);

  // after
  const activeCourse = selectedCourses[activeRound];
  const advanceHours =
    bookingSettings?.blocked_advance ?? DEFAULT_ADVANCE_HOURS;
  const advanceDays = Math.ceil(advanceHours / 24);
  const firstBookableDate = new Date(today);
  firstBookableDate.setDate(firstBookableDate.getDate() + advanceDays);
  const sameRoundTimeBlocks = activeCourse?.block_other_round_times
    ? Object.entries(selectedTimes)
        .filter(
          ([idx, time]) =>
            Number(idx) !== activeRound &&
            time &&
            selectedCourses[Number(idx)]?.id === activeCourse.id,
        )
        .map(([, time]) => time)
    : [];

  const terminalBlocks =
    activeRound === 0 && activeCourse
      ? (() => {
          const earliest = getEarliestTeeTime(activeCourse.terminal_group);
          if (!earliest || !activeCourse.tee_slots) return [];
          return activeCourse.tee_slots
            .filter((s) => s.time < earliest)
            .map((s) => s.time);
        })()
      : [];

  const blockedTeeTimes = [...sameRoundTimeBlocks, ...terminalBlocks];

  const handleSetDate = (roundIndex, date) => {
    // Rounds always run on consecutive days. Whichever round's date was just
    // edited becomes the new anchor, and every other round (not just the ones
    // still unset) is recomputed relative to it — otherwise editing a later
    // round's date leaves earlier auto-filled rounds out of sync.
    setSelectedDates(() => {
      const next = {};
      for (let i = 0; i < numRounds; i++) {
        next[i] = date ? addDays(date, i - roundIndex) : undefined;
      }
      return next;
    });
  };

  // scrollIntoView({ behavior: "smooth" }) animates over several frames.
  // Opening a Radix popover in the same tick anchors it to the trigger's
  // pre-scroll position — often off-screen — which then gets clamped to
  // a viewport corner instead of tracking the trigger. Waiting for the
  // scroll to actually finish (with a timeout fallback for browsers
  // without "scrollend", or if the target was already in view and no
  // scroll fires at all) keeps the popover glued to its trigger.
  const scrollThenOpen = (openFn) => {
    let settled = false;
    const settle = () => {
      if (settled) return;
      settled = true;
      window.removeEventListener("scrollend", settle);
      openFn(true);
    };
    if ("onscrollend" in window) {
      window.addEventListener("scrollend", settle, { once: true });
    }
    setTimeout(settle, 500);
  };

  // Clicking a locked (no-date-yet) club card doesn't just silently
  // refuse — it scrolls the date picker into view, pops it open, and
  // pulses its trigger for a moment so the reason is obvious.
  const promptDateSelection = () => {
    activeRoundBarRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "center",
    });
    scrollThenOpen(setDateOpen);
    setDateAttention(true);
    setTimeout(() => setDateAttention(false), 1200);
  };

  // Picking a golf club for the active round immediately opens the tee
  // time popover, so the user flows straight from course → time.
  const handleSelectCourse = (roundIndex, course) => {
    // Courses (and by extension tee times) depend on the round's date —
    // availability/blocked-date checks only make sense once a date is
    // picked. Cards stay visible but locked pre-date (see roundCourses
    // render below); this guard just makes the rule hold regardless of
    // entry point.
    if (!selectedDates[roundIndex]) {
      if (roundIndex === activeRound) promptDateSelection();
      return;
    }
    setSelectedCourses((prev) => ({ ...prev, [roundIndex]: course }));
    if (roundIndex === activeRound) {
      activeRoundBarRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
      scrollThenOpen(setTeeTimeOpen);
    }
  };

  // Setting a round's tee time completes that round — auto-advance to the
  // next round's pill so the flow moves one round at a time.
  const handleSetTime = (roundIndex, time) => {
    setSelectedTimes((prev) => ({ ...prev, [roundIndex]: time }));
    if (roundIndex === activeRound && roundIndex < numRounds - 1) {
      setActiveRound(roundIndex + 1);
    }
  };

  const allComplete = Array.from({ length: numRounds }).every(
    (_, i) => selectedCourses[i] && selectedDates[i],
  );
  const allTimesComplete = Array.from({ length: numRounds }).every(
    (_, i) => selectedTimes[i],
  );

  // Once every round has a course, date and tee time, jump the golfer
  // straight down to Recommended Travel instead of leaving them to
  // scroll and hunt for it themselves. Guarded by a ref so it only
  // fires on the transition into "complete", not on every re-render.
  useEffect(() => {
    if (allComplete && allTimesComplete) {
      if (!hasAutoScrolledToTravelRef.current) {
        hasAutoScrolledToTravelRef.current = true;
        const t = setTimeout(() => {
          travelStepRef.current?.scrollIntoView({
            behavior: "smooth",
            block: "start",
          });
        }, 300);
        return () => clearTimeout(t);
      }
    } else {
      hasAutoScrolledToTravelRef.current = false;
    }
  }, [allComplete, allTimesComplete]);

  if (loadingPkg) {
    return (
      <div className="min-h-screen bg-[#0B2421] flex items-center justify-center">
        <div className="text-white/60 text-lg">Loading...</div>
      </div>
    );
  }

  if (!pkg) {
    return (
      <div className="min-h-screen bg-[#0B2421] flex items-center justify-center">
        <div className="text-center">
          <p className="text-white/60 text-lg mb-4">Package not found.</p>
          <button
            onClick={() => navigate(-1)}
            className="text-[#D4AF37] font-semibold hover:underline"
          >
            Go back
          </button>
        </div>
      </div>
    );
  }

  // Only show the golf clubs the admin actually assigned to this gateway —
  // a single shared pool, not split per round.
  const roundCourseIds = normalizeSelectedCourses(pkg.selectedCourses).map(
    (c) => c.id,
  );

  const roundCourses = allCourses
    .filter((c) => roundCourseIds.includes(c.id))
    .filter((c) => !isCourseBlockedOnDate(c, selectedDates[activeRound]));

  const roundCoursesArr = Array.from({ length: numRounds }).map(
    (_, i) => selectedCourses[i],
  );
  const roundDatesArr = Array.from({ length: numRounds }).map(
    (_, i) => selectedDates[i],
  );
  const roundTimesArr = Array.from({ length: numRounds }).map(
    (_, i) => selectedTimes[i],
  );
  // Golf-only total, shown once every round is picked so the golfer sees
  // what the rounds cost before travel is added on.
  const golfTotal = calculateGolfTotal({
    rounds: roundCoursesArr.map((course, i) => ({
      course,
      date: roundDatesArr[i],
      period: deriveTeePeriod(roundTimesArr[i]),
    })),
    totalGuests: numPlayers,
  });

  const packageSummary = {
    id: pkg.id,
    title: pkg.destination,
    rounds: numRounds,
    nights: numNights,
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Back bar */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-40 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-1.5 text-sm text-gray-600 hover:text-[#0B2421] transition-colors"
          >
            <ChevronLeft className="h-4 w-4" />
            Back to Packages
          </button>
        </div>
      </div>

      {/* Hero */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="relative h-[420px] sm:h-[500px]"
      >
        <img
          src={pkg.image}
          alt={pkg.title}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 p-6 sm:p-10 max-w-7xl mx-auto">
          <p className="text-[#D4AF37] text-sm font-semibold uppercase tracking-widest mb-2">
            {pkg.destination}
          </p>
          <h1 className="text-white font-bold text-4xl sm:text-5xl mb-3">
            {pkg.title}
          </h1>
          <p className="text-white/70 text-base sm:text-lg max-w-xl">
            {pkg.tagline}
          </p>
          <div className="flex flex-wrap items-center gap-4 mt-4">
            <span className="flex items-center gap-1.5 text-white/80 text-sm">
              <Clock className="h-4 w-4" />
              {pkg.duration}
            </span>
            <span className="flex items-center gap-1.5 text-white/80 text-sm">
              <Flag className="h-4 w-4" />
              {numRounds} Rounds of Golf
            </span>
            <span className="flex items-center gap-1.5 text-white/80 text-sm">
              <Bed className="h-4 w-4" />
              {numNights} Nights
            </span>
            <span className="flex items-center gap-1.5 text-white/80 text-sm">
              <MapPin className="h-4 w-4" />
              {pkg.destination}
            </span>
          </div>
        </div>
      </motion.div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div>
          {/* Content */}
          <div className="space-y-8">
            {/* Round Selection + Golf Club Picker */}
            {/* Opacity-only fade — a translateY animation here would leave a lingering
                CSS transform on this ancestor, which breaks position:sticky on the
                child below (a transformed ancestor becomes its own containing block). */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.05 }}
            >
              {/* Sticky on mobile: header, players/rounds steppers, round tabs, and active round bar stay pinned while the club grid scrolls beneath */}
              <div className="sticky top-[81px] z-30 bg-white pt-2 pb-1.5 -mt-2 sm:static sm:pt-0 sm:pb-0 sm:mt-0">
                <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
                  <h2 className="text-[#0B2421] font-bold text-xl">
                    Select Your Golf Clubs
                  </h2>
                  <div className="flex items-center gap-3 flex-wrap">
                    {/* Custom rounds stepper — only for "Build Your Own" */}
                    {pkg.custom && (
                      <div className="flex items-center gap-3 bg-[#0B2421]/5 border border-[#0B2421]/10 rounded-xl px-3 py-2">
                        <span className="text-sm font-semibold text-[#0B2421]">
                          Number of Rounds
                        </span>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() =>
                              setCustomRounds((n) => Math.max(1, n - 1))
                            }
                            className="w-7 h-7 rounded-lg border border-gray-200 flex items-center justify-center text-gray-600 hover:border-[#0B2421] transition-colors disabled:opacity-30"
                            disabled={customRounds <= 1}
                          >
                            −
                          </button>
                          <span className="text-[#0B2421] font-bold text-base w-5 text-center">
                            {customRounds}
                          </span>
                          <button
                            onClick={() =>
                              setCustomRounds((n) =>
                                Math.min(MAX_CUSTOM_ROUNDS, n + 1),
                              )
                            }
                            className="w-7 h-7 rounded-lg border border-gray-200 flex items-center justify-center text-gray-600 hover:border-[#0B2421] transition-colors disabled:opacity-30"
                            disabled={customRounds >= MAX_CUSTOM_ROUNDS}
                          >
                            +
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Players stepper — always visible, no longer gated behind a confirm panel */}
                    <div className="flex items-center gap-3 bg-[#0B2421]/5 border border-[#0B2421]/10 rounded-xl px-3 py-2">
                      <Users className="h-4 w-4 text-[#0B2421]" />
                      <span className="text-sm font-semibold text-[#0B2421]">
                        Players
                      </span>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() =>
                            setNumPlayers((n) => Math.max(1, n - 1))
                          }
                          className="w-7 h-7 rounded-lg border border-gray-200 flex items-center justify-center text-gray-600 hover:border-[#0B2421] transition-colors disabled:opacity-30"
                          disabled={numPlayers <= 1}
                        >
                          −
                        </button>
                        <span className="text-[#0B2421] font-bold text-base w-5 text-center">
                          {numPlayers}
                        </span>
                        <button
                          onClick={() =>
                            setNumPlayers((n) => Math.min(8, n + 1))
                          }
                          className="w-7 h-7 rounded-lg border border-gray-200 flex items-center justify-center text-gray-600 hover:border-[#0B2421] transition-colors disabled:opacity-30"
                          disabled={numPlayers >= 8}
                        >
                          +
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Step-by-step instructions — tracks the active round's progress live.
                    The active round's date + tee time pickers live in this same
                    grey bar, stacked on the right. */}
                <div
                  ref={activeRoundBarRef}
                  className="mb-4 rounded-xl border border-[#0B2421]/10 bg-[#0B2421]/5 px-4 py-3 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between"
                >
                  <div className="min-w-0 flex-1">
                    <p className="text-xs font-bold text-[#0B2421] uppercase tracking-wide mb-2">
                      {t.instructionsHeading}
                    </p>
                    <ol className="flex flex-col sm:flex-row sm:flex-wrap gap-x-6 gap-y-1.5">
                      {[
                        {
                          done: !!selectedDates[activeRound],
                          label: fillTemplate(t.stepSelectDate, {
                            round: activeRound + 1,
                          }),
                        },
                        {
                          done: !!selectedCourses[activeRound],
                          label: fillTemplate(t.stepSelectCourse, {
                            round: activeRound + 1,
                          }),
                        },
                        {
                          done: !!selectedTimes[activeRound],
                          label: fillTemplate(t.stepSelectTime, {
                            round: activeRound + 1,
                          }),
                        },
                      ].map((step, i) => (
                        <li key={i} className="flex items-center gap-2 text-sm">
                          <span
                            className={`flex items-center justify-center w-5 h-5 rounded-full text-[10px] font-bold shrink-0 ${
                              step.done
                                ? "bg-emerald-600 text-white"
                                : "bg-white border border-[#0B2421]/30 text-[#0B2421]"
                            }`}
                          >
                            {step.done ? <Check className="w-3 h-3" /> : i + 1}
                          </span>
                          <span
                            className={
                              step.done
                                ? "text-emerald-700 line-through decoration-emerald-400/60"
                                : "text-[#0B2421] font-medium"
                            }
                          >
                            {step.label}
                          </span>
                        </li>
                      ))}
                    </ol>
                    <p className="text-xs text-[#0B2421]/60 mt-2">
                      Bookings can only be made for dates starting {advanceDays}{" "}
                      day{advanceDays !== 1 ? "s" : ""} from now.
                    </p>
                  </div>

                  {/* Active round date + tee time — stacked on top of each other */}
                  <div className="flex flex-col gap-2 shrink-0 sm:items-end">
                    {/* Per-round date picker */}
                    <Popover
                      open={dateOpen}
                      onOpenChange={(open) => {
                        setDateOpen(open);
                        if (open) setDateAttention(false);
                      }}
                    >
                      <PopoverTrigger asChild>
                        <motion.button
                          animate={
                            dateAttention
                              ? { scale: [1, 1.07, 1] }
                              : { scale: 1 }
                          }
                          transition={{
                            duration: 0.4,
                            repeat: dateAttention ? 2 : 0,
                          }}
                          className={`flex items-center gap-2 bg-white border rounded-lg px-3 py-2 text-sm transition-colors w-full sm:w-[200px] ${
                            dateAttention
                              ? "border-[#D4AF37] ring-4 ring-[#D4AF37]/30"
                              : !selectedDates[activeRound]
                                ? "border-[#D4AF37] ring-2 ring-[#D4AF37]/20 animate-pulse"
                                : "border-gray-200 hover:border-[#0B2421]"
                          }`}
                        >
                          <CalendarIcon className="h-4 w-4 text-[#D4AF37] shrink-0" />
                          <span
                            className={
                              selectedDates[activeRound]
                                ? "text-[#0B2421] font-medium"
                                : "text-gray-400"
                            }
                          >
                            {selectedDates[activeRound]
                              ? format(selectedDates[activeRound], "d MMM yyyy")
                              : fillTemplate(t.roundDateTrigger, {
                                  round: activeRound + 1,
                                })}
                          </span>
                          <ChevronDown className="h-3.5 w-3.5 text-gray-400 ml-auto shrink-0" />
                        </motion.button>
                      </PopoverTrigger>
                      <AnyPopoverContent
                        className="w-auto p-0"
                        align="end"
                        sideOffset={8}
                      >
                        <Calendar
                          mode="single"
                          minYear={new Date().getFullYear()}
                          maxYear={new Date().getFullYear() + 5}
                          endMonth={new Date(new Date().getFullYear() + 5, 11)}
                          selected={selectedDates[activeRound]}
                          onSelect={(d) => {
                            handleSetDate(activeRound, d);
                            setDateOpen(false);
                          }}
                          disabled={(date) => {
                            const d = new Date(date);
                            d.setHours(0, 0, 0, 0);

                            return d < firstBookableDate;
                          }}
                        />
                      </AnyPopoverContent>
                    </Popover>

                    {/* Per-round tee time picker — pop-up, AM/PM toggle, 15-min slots */}
                    <TeeTimePopover
                      value={selectedTimes[activeRound]}
                      onChange={(t) => handleSetTime(activeRound, t)}
                      disabled={
                        !selectedDates[activeRound] ||
                        !selectedCourses[activeRound]
                      }
                      label={fillTemplate(t.roundTeeTimeTrigger, {
                        round: activeRound + 1,
                      })}
                      teeSlots={activeCourse?.tee_slots}
                      blockedTimes={blockedTeeTimes}
                      bookedCounts={slotBookedCounts}
                      open={teeTimeOpen}
                      onOpenChange={setTeeTimeOpen}
                    />
                  </div>
                </div>

                {/* Round selector pills */}
                <div className="flex gap-1.5 sm:gap-3 mb-5">
                  {Array.from({ length: numRounds }).map((_, i) => {
                    const picked = selectedCourses[i];
                    const period = deriveTeePeriod(selectedTimes[i]);
                    const roundPrice =
                      picked && selectedDates[i]
                        ? calculateCourseRoundPrice({
                            course: picked,
                            date: selectedDates[i],
                            period,
                            totalPlayers: numPlayers,
                            totalRounds: numRounds,
                          })
                        : null;
                    const isActive = activeRound === i;
                    const isComplete = !!(
                      picked &&
                      selectedDates[i] &&
                      selectedTimes[i]
                    );
                    return (
                      <button
                        key={i}
                        title={picked ? picked.name : `Round ${i + 1}`}
                        onClick={() => setActiveRound(i)}
                        className={`flex items-center gap-1.5 sm:gap-2.5 px-2 py-2 sm:px-4 sm:py-3 rounded-xl border-2 text-xs sm:text-sm font-semibold transition-all flex-1 min-w-0 sm:flex-none sm:w-[150px] sm:shrink-0 ${
                          isActive
                            ? "border-[#0B2421] bg-[#0B2421] text-white shadow-md"
                            : isComplete
                              ? "border-emerald-600 bg-emerald-50 text-emerald-700"
                              : "border-gray-200 bg-white text-gray-500 hover:border-gray-400"
                        }`}
                      >
                        {isComplete ? (
                          <Check className="w-3.5 h-3.5 sm:w-4 sm:h-4 shrink-0" />
                        ) : (
                          <div
                            className={`w-4 h-4 sm:w-5 sm:h-5 rounded-full border-2 flex items-center justify-center shrink-0 ${isActive ? "border-white/60" : "border-gray-300"}`}
                          >
                            <span className="text-[9px] sm:text-[10px] font-bold">
                              {i + 1}
                            </span>
                          </div>
                        )}
                        <span className="flex flex-col items-start min-w-0 flex-1">
                          <span className="truncate w-full text-left">
                            {picked ? picked.name : `Round ${i + 1}`}
                          </span>
                          <span className="flex items-baseline justify-between w-full gap-1">
                            {selectedDates[i] && (
                              <span
                                className={`text-[10px] font-normal truncate ${isActive ? "text-white/70" : isComplete ? "text-emerald-600/70" : "text-gray-400"}`}
                              >
                                {format(selectedDates[i], "d MMM")}
                                {selectedTimes[i]
                                  ? ` · ${selectedTimes[i]} WIB`
                                  : ""}
                              </span>
                            )}
                            {roundPrice != null && (
                              <span
                                className={`text-[10px] font-bold shrink-0 ${isActive ? "text-white" : isComplete ? "text-emerald-700" : "text-gray-500"}`}
                              >
                                {formatSGD(roundPrice)}
                              </span>
                            )}
                          </span>
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Golf club cards — stacked one below the other on mobile,
                  a full grid from sm: up. Reacts to whichever round is active. */}
              <AnimatePresence mode="wait">
                <motion.div
                  key={activeRound}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -8 }}
                  transition={{ duration: 0.2 }}
                >
                  <div className="flex flex-col gap-4 sm:grid sm:grid-cols-3">
                    {roundCourses.length === 0 && (
                      <p className="sm:col-span-3 text-sm text-gray-400 py-8 text-center">
                        {t.noClubsAssigned}
                      </p>
                    )}
                    {roundCourses.map((course) => {
                      const isSelected =
                        selectedCourses[activeRound]?.id === course.id;
                      const locked = !selectedDates[activeRound];
                      const cardPrice = calculateCourseRoundPrice({
                        course,
                        date: selectedDates[activeRound],
                        period: activePeriod,
                        totalPlayers: numPlayers,
                        totalRounds: numRounds,
                      });
                      return (
                        <div
                          key={course.id}
                          onClick={() =>
                            handleSelectCourse(activeRound, course)
                          }
                          aria-disabled={locked}
                          className={`rounded-2xl overflow-hidden border-2 transition-all shadow-sm ${
                            locked
                              ? "cursor-pointer opacity-60 grayscale-[35%] border-gray-100"
                              : "cursor-pointer hover:shadow-md " +
                                (isSelected
                                  ? "border-[#0B2421]"
                                  : "border-gray-100 hover:border-gray-300")
                          }`}
                        >
                          <div className="relative h-32 overflow-hidden">
                            <img
                              src={course.image}
                              alt={course.name}
                              className="w-full h-full object-cover"
                            />

                            <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />

                            {locked ? (
                              <div className="absolute top-3 left-3 w-7 h-7 bg-black/40 backdrop-blur-sm rounded-full flex items-center justify-center text-white z-10">
                                <Lock className="w-3.5 h-3.5" />
                              </div>
                            ) : (
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setInfoCourse(course);
                                }}
                                aria-label={`More info about ${course.name}`}
                                className="absolute top-3 left-3 w-7 h-7 bg-black/40 hover:bg-black/60 backdrop-blur-sm rounded-full flex items-center justify-center text-white transition-colors z-10"
                              >
                                <Info className="w-4 h-4" />
                              </button>
                            )}

                            {/* Selected Check — course friendliness now lives in the info drawer instead */}
                            <div className="absolute top-3 right-3 flex items-center space-x-2 z-10">
                              {isSelected && !locked && (
                                <div className="w-7 h-7 bg-[#0B2421] rounded-full flex items-center justify-center">
                                  <Check className="w-4 h-4 text-[#D4AF37]" />
                                </div>
                              )}
                            </div>

                            <h3 className="absolute bottom-3 left-3 text-white font-bold text-sm leading-snug pr-10">
                              {course.name}
                            </h3>
                          </div>

                          <div className="p-3 bg-white">
                            <div className="flex items-center justify-between mb-2">
                              <span className="flex items-center gap-1 text-xs text-gray-500 truncate">
                                <MapPin className="w-3 h-3 shrink-0" />
                                <span className="truncate">{course.city}</span>
                              </span>
                              <span className="flex items-center gap-1 text-xs text-amber-500 font-semibold shrink-0">
                                <Star className="w-3 h-3 fill-amber-400" />
                                {course.rating}
                              </span>
                            </div>
                            <div className="flex flex-wrap gap-1 mb-2">
                              {(course.fees_included || []).map((f) => (
                                <span
                                  key={f}
                                  className="text-[10px] bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full"
                                >
                                  {f}
                                </span>
                              ))}
                            </div>
                            <div className="flex items-center justify-between pt-2 border-t border-gray-100 gap-2">
                              <div className="min-w-0">
                                {/* <p className="text-[10px] text-gray-400">
                                  From
                                </p> */}
                                <p className="text-[#0B2421] font-bold text-base truncate">
                                  {formatSGD(cardPrice)}
                                  <span className="text-xs font-normal text-gray-400">
                                    {" "}
                                    per player
                                  </span>
                                </p>
                              </div>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleSelectCourse(activeRound, course);
                                }}
                                className={`px-4 py-2 rounded-lg text-xs font-bold transition-colors shrink-0 flex items-center gap-1.5 ${
                                  locked
                                    ? "bg-gray-200 text-gray-500"
                                    : isSelected
                                      ? "bg-emerald-700 text-white"
                                      : "bg-[#0B2421] text-white hover:bg-[#0B2421]/85"
                                }`}
                              >
                                {locked && <Lock className="w-3 h-3" />}

                                {locked
                                  ? t.clubCardLocked
                                  : isSelected
                                    ? t.clubCardSelected
                                    : t.clubCardSelect}
                              </button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </motion.div>
              </AnimatePresence>
            </motion.div>

            {/* ── Recommended Travel ── appears below, on this same page, once every round is set ── */}
            {allComplete && allTimesComplete && (
              <div ref={travelStepRef}>
                <motion.div
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3 }}
                  className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5"
                >
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-8 h-8 rounded-full bg-[#0B2421] flex items-center justify-center shrink-0">
                      <Flag className="h-4 w-4 text-[#D4AF37]" />
                    </div>
                    <h2 className="text-[#0B2421] font-bold text-lg">
                      Golf Total
                    </h2>
                  </div>
                  <div className="flex items-end justify-between gap-4">
                    <div className="space-y-1 text-sm text-gray-600">
                      <p className="flex items-center gap-1.5">
                        <Users className="h-3.5 w-3.5 text-gray-400" />
                        {numPlayers} Player{numPlayers > 1 ? "s" : ""}
                      </p>
                      <p className="flex items-center gap-1.5">
                        <Flag className="h-3.5 w-3.5 text-gray-400" />
                        {numRounds} Round{numRounds > 1 ? "s" : ""}
                      </p>
                    </div>
                    <p className="text-[#0B2421] font-bold text-xl text-right">
                      {formatSGD(golfTotal)} Total
                    </p>
                  </div>
                </motion.div>
                <RecommendedTravelStep
                  pkg={packageSummary}
                  numPlayers={numPlayers}
                  selectedCourses={roundCoursesArr}
                  inboundDate={roundDatesArr[0]}
                  inboundTeeTime={roundTimesArr[0]}
                  outboundDate={roundDatesArr[roundDatesArr.length - 1]}
                  outboundTeeTime={roundTimesArr[roundTimesArr.length - 1]}
                  roundDates={roundDatesArr}
                  roundTimes={roundTimesArr}
                  onSelectionChange={setTravelSelections}
                  onContinue={() => {
                    navigate("/package-checkout", {
                      state: {
                        selectedCourses: roundCoursesArr,
                        selectedAccommodation:
                          travelSelections.selectedAccommodation,
                        sharingType: travelSelections.sharingType,
                        numRooms: travelSelections.numRooms,
                        checkInDate: travelSelections.checkInDate,
                        checkInTime: travelSelections.checkInTime,
                        checkOutDate: travelSelections.checkOutDate,
                        selectedFerry: travelSelections.selectedFerry,
                        selectedFerryTime: travelSelections.selectedFerryTime,
                        selectedOutboundFerryTime:
                          travelSelections.selectedOutboundFerryTime,
                        selectedTransport: travelSelections.selectedTransport,
                        selectedTransportTime:
                          travelSelections.selectedTransportTime,
                        selectedOutboundTransport:
                          travelSelections.selectedOutboundTransport,
                        selectedOutboundTransportTime:
                          travelSelections.selectedOutboundTransportTime,
                        selectedPackage: packageSummary,
                        numPlayers,
                        roundDates: roundDatesArr,
                        roundTimes: roundTimesArr,
                        startDate: roundDatesArr[0],
                        // onBack: () => setStep("golfers"),
                        endDate: roundDatesArr[roundDatesArr.length - 1],
                      },
                    });
                  }}
                />
              </div>
            )}
          </div>
        </div>
      </div>

      <CourseInfoDrawer
        course={infoCourse}
        onClose={() => setInfoCourse(null)}
      />
    </div>
  );
}